<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    icon="fa-truck"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!loading && !loadingError">
      <FormRow label="Type">
        <FormStaticLabel><LabelBadge :labelClass="`label-taskitem-type-${taskItem.type}`">{{taskItem.type | formatLookupName(TaskItemTypeLookup)}}</LabelBadge></FormStaticLabel>
      </FormRow>
      <FormRow label="Name">
        <FormInputText v-model="taskItem.name" placeholder="Name" field="name" />
      </FormRow>
      <FormRow label="Details">
        <FormInputText v-model="taskItem.details" placeholder="Details / Instructions" field="details" />
      </FormRow>
      <FormRow label="Location" v-if="taskItem.isLocationEnabled && isTaskItemInternalTransfer">
        <AccountLocationSelect v-model="taskItem.accountLocationId" field="accountLocationId" />
      </FormRow>
      <FormRow label="Location" v-if="taskItem.isLocationEnabled && !isTaskItemInternalTransfer">
        <FormInputAddressSearch v-model="taskItem.locationAddress" placeholder="Address" field="locationAddress" :splitAddressComponents="false" />
      </FormRow>
      <FormRow label="Duration" helpTip="Estimated duration, not including travel time">
        <FormInputText type="number" v-model="taskItem.estimatedProcessingTime" placeholder="Duration" field="estimatedProcessingTime" addon="mins" />
      </FormRow>
      <FormRow label="Stoppage Labels" v-if="isTaskItemStoppage">
        <FormLabelTag popupName="Stoppage Labels" labelType="taskItemStoppage" field="stoppageLabels" v-model="taskItem.stoppageLabels" />
      </FormRow>

      <div v-if="hasLinkedItems">
        <FormHorizontalRule />
        <ContentTitle>Items</ContentTitle>

        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th width="20%">Type</th>
              <th width="15%">Info</th>
              <th width="60%">Details</th>
              <th width="10%" v-if="isOperationalReportingEnabled"><Tooltip label="Operational Reporting details per order" helpStyle>Operational</Tooltip></th>
              <th width="50px">Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="linkedItem of taskItem.taskItemLinkedItems" :key="`linkedItem-${linkedItem.id}`">
              <td>
                {{formatLookupName(linkedItem.type, TaskLinkedItemTypeLookup)}}
                <span v-if="linkedItem.order != null"> - </span>
                <LinkOrder v-if="linkedItem.order != null" :id="linkedItem.order.id" :name="linkedItem.order.sequenceNumber" />
              </td>
              <td>
                <LinkContact v-if="linkedItem.order != null" :id="linkedItem.order.contact.id" :name="linkedItem.order.contact.name" />
                <span v-if="isStockTransfer(taskItem, linkedItem)">
                  <LinkStock :id="linkedItem.stock.id" :name="linkedItem.stock.name" />
                </span>
              </td>
              <td>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.orderItem">
                  {{linkedItem.orderItem.quantity}}x {{linkedItem.orderItem.description}} {{linkedItem.orderItem.generatedDescription}}<span v-if="isOrderItemGlass(linkedItem.orderItem)"> - {{linkedItem.orderItem.glassHeight}}x{{linkedItem.orderItem.glassWidth}}</span>
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.purchaseOrderItem">
                  {{linkedItem.purchaseOrderItem.quantity}}x {{linkedItem.purchaseOrderItem.description}}<span v-if="isPurchaseOrderItemGlass(linkedItem.purchaseOrderItem)"> - {{linkedItem.purchaseOrderItem.glassHeight}}x{{linkedItem.purchaseOrderItem.glassWidth}}</span>
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.productionPanel">
                  {{linkedItem.productionPanel.barcode}} - {{linkedItem.productionPanel.panelHeight}} x {{linkedItem.productionPanel.panelWidth}} - {{linkedItem.orderItem.description}} {{linkedItem.orderItem.generatedDescription}}
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.general">
                  <UOMValue :value="linkedItem.quantity" :uomType="linkedItem.stock.uomType" />
                </span>
                <span v-if="linkedItem.type == TaskLinkedItemTypes.glassLevel">
                  {{linkedItem.quantity}}x {{linkedItem.glassLevel.height}} x {{linkedItem.glassLevel.width}}
                </span>
              </td>
              <td v-if="isOperationalReportingEnabled">
                <div v-if="isOperationalCodeDisplayed(task.taskType, taskItem, linkedItem)">
                  <LabelBadge :class="`task-operational-category-${taskOpCodeCategoryLookup(linkedItem.operationalReportingCode)}`">{{formatLookupName(linkedItem.operationalReportingCode, ItemOperationalReportingCodeLookup)}}</LabelBadge>
                  <a class="table-item-editable-button ml-2" v-if="true" @click.prevent="displayOperationalCodeEdit($event, taskItem, linkedItem)"><i class="far fa-pencil"></i></a>
                </div>
              </td>
              <td>
                <ActionDropMenu :tableAction="true">
                  <DropdownMenuItem @click="removeTaskItemLinkedItem(linkedItem.id)" icon="fa-trash-alt">Cancel Item</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
    </FormGroup>    

    <TaskOperationalCodePopover
      ref="operationalCodeEditPopover"
      :order="operationalCodePopoverInfo.order"
      :taskScheduleStartDate="task.scheduleStartDate"
      :codeValue="operationalCodePopoverInfo.linkedItem ? operationalCodePopoverInfo.linkedItem.operationalReportingCode : null"
      :codeNote="operationalCodePopoverInfo.linkedItem ? operationalCodePopoverInfo.linkedItem.operationalReportingNote : null"
      @update:codeValue="updateOpCodeValue($event)"
      @update:codeNote="updateOpCodeNote($event)"
      @resetCode="resetOpCode()"
    />

  </ModalDialog>
</template>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import TaskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import TaskOperationalCodePopover from '@/modules/tasks/components/taskOperationalCodePopover'

import { TaskItemTypeLookup, TaskItemTypes, TaskLinkedItemTypes, TaskLinkedItemTypeLookup, InventoryStockTypes, ItemOperationalReportingCodeLookup } from '@/store/types'

export default {
  name: 'TaskItemEditDialog',
  mixins: [
    ModalDialogMixin,
    form,
    TaskDespatchMixin
  ],
  components: {
    ModalDialog,
    AccountLocationSelect,
    TaskOperationalCodePopover
  },
  props: {
    isCreateMode: Boolean,
    taskId: Number,
    taskItemId: Number,
  },
  data() {
    return {
      responseInfo: {},
      dialogForm: {},

      submitting: false,
      loading: false,
      loadingError: '',

      task: {},
      taskItem: {},

      operationalCodePopoverInfo: {
        taskItem: null,
        linkedItem: null,
        order: null
      }
    }
  },
  watch: {
    'taskItem.accountLocationId': function() {
      // Fill in address
      var accLoc = this.$store.getters['account/getAccLocationById'](this.taskItem.accountLocationId)
      if(accLoc) {
        this.taskItem.locationAddress = accLoc.formattedAddress
      }
    }
  },
  created() {
    this.TaskItemTypeLookup = TaskItemTypeLookup
    this.TaskLinkedItemTypes = TaskLinkedItemTypes
    this.TaskLinkedItemTypeLookup = TaskLinkedItemTypeLookup
    this.ItemOperationalReportingCodeLookup = ItemOperationalReportingCodeLookup
  },
  mounted() {
    
  },
  computed: {
    title() {
      return this.isCreateMode ? 'Create Runsheet Item' : `Update Runsheet Item`
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    hasLinkedItems() {
      return _.isArray(this.taskItem.taskItemLinkedItems) && this.taskItem.taskItemLinkedItems.length > 0
    },
    isTaskItemStoppage() {
      return this.taskItem.type == TaskItemTypes.stoppage
    },
    isTaskItemInternalTransfer() {
      return this.taskItem.type == TaskItemTypes.transferInternal
    },
    isOperationalReportingEnabled() {
      if(this.task.taskType)
        return this.task.taskType.isOperationalReportingEnabled
      return false
    }
  },
  methods: {
    show(taskItem = {}) {
      // Reset form
      this.responseInfo = { }
      this.dialogForm = { }

      this.requestData()

      // Default task item if creating it
      if(this.isCreateMode) {
        this.taskItem = {
          type: taskItem.type,
          name: taskItem.name,
          details: taskItem.details || '',
          estimatedProcessingTime: taskItem.estimatedProcessingTime,
          isLocationEnabled: taskItem.isLocationEnabled,
          accountLocationId: taskItem.accountLocationId,
          locationAddress: '',
          stoppageLabels: [],
          taskItemLinkedItems: []
        }
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.tasksService.getTask(this.taskId),
      ])
      .then(results => {
        this.task = results[0].data
        if(!this.isCreateMode)
          this.taskItem = this.task.taskItems.find(taskItem => taskItem.id == this.taskItemId)

      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    isStockTransfer(taskItem, linkedItem) {
      if(taskItem.type == TaskItemTypes.transferInternal || taskItem.type == TaskItemTypes.transferExternal) {
        return linkedItem.type == TaskLinkedItemTypes.general || linkedItem.type == TaskLinkedItemTypes.glassLevel
      }
      return false
    },
    isOrderItemGlass(orderItem) {
      return orderItem.stockType == InventoryStockTypes.glass
    },
    isPurchaseOrderItemGlass(purchaseOrderItem) {
      return purchaseOrderItem.stockType == InventoryStockTypes.glass
    },
    removeTaskItemLinkedItem(linkedItemId) {
      this.taskItem.taskItemLinkedItems = this.taskItem.taskItemLinkedItems.filter(linkedItem => linkedItem.id != linkedItemId)
    },
    displayOperationalCodeEdit(evt, taskItem, linkedItem) {
      this.$refs.operationalCodeEditPopover.displayFromElement(evt.target)

      this.operationalCodePopoverInfo.taskItem = taskItem
      this.operationalCodePopoverInfo.linkedItem = linkedItem
      this.operationalCodePopoverInfo.order = linkedItem.order
    },
    updateOpCodeValue(value) {
      this.operationalCodePopoverInfo.linkedItem.operationalReportingCode = value

      // Update all linked items for the same order in this task item with the same code
      if(this.operationalCodePopoverInfo.taskItem && this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems) {
        this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingCode = value
          }
        })
      }
    },
    updateOpCodeNote(note) {
      this.operationalCodePopoverInfo.linkedItem.operationalReportingNote = note

      // Update all linked items for the same order in this task item with the same code
      if(this.operationalCodePopoverInfo.taskItem && this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems) {
        this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingNote = note
          }
        })
      }
    },
    resetOpCode() {
      // Reset back to calculated code
      var opCodeInfo = this.calculateOperationalInfo(this.task, [], TaskLinkedItemTypes.order, this.operationalCodePopoverInfo.order)

      // Reset all linked items for the same order in this task item with the same code
      if(this.operationalCodePopoverInfo.taskItem && this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems) {
        this.operationalCodePopoverInfo.taskItem.taskItemLinkedItems.forEach(linkedItem => {
          if(linkedItem.orderId == this.operationalCodePopoverInfo.linkedItem.orderId) {
            linkedItem.operationalReportingCode = opCodeInfo.code
          }
        })
      }
    },

    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = {
        type: this.taskItem.type,
        name: this.taskItem.name,
        details: this.taskItem.details,
        isLocationEnabled: this.taskItem.isLocationEnabled,
        locationAddress: this.taskItem.locationAddress,
        accountLocationId: this.taskItem.accountLocationId,
        estimatedProcessingTime: this.taskItem.estimatedProcessingTime,
        taskItemLinkedItems: this.taskItem.taskItemLinkedItems,
        stoppageLabels: this.taskItem.stoppageLabels.map(label => label.id)
      }

      if(this.isCreateMode) {
        this.$apiService.tasksService.createTaskItem(this.taskId, updateData)
        .then(response => {
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.$data.dialogForm = this.processFormResponse(err)
        })
        .finally(() => {
          this.$data.submitting = false
        })

      } else {
        this.$apiService.tasksService.updateTaskItem(this.taskId, this.taskItemId, updateData)
        .then(response => {
          this.modalClosed(true)
          this.hide()
        })
        .catch(err => {
          this.$data.dialogForm = this.processFormResponse(err)
        })
        .finally(() => {
          this.$data.submitting = false
        })
      }
    }
  }
}
</script>